// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CreateCostAdjustmentParameters } from '@/asset/api/create-cost-adjustment';
import {
  FilterToggleGroup,
  FilterToggleGroupProps,
} from '@/components/filter-toggle-group';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import {
  COST_ADJUSTMENT_TYPE_OPTIONS,
  CostAdjustmentType,
  costAdjustmentTypeSchema,
  getCostAdjustmentReasonOptions,
} from '@/constants/cost-adjustment';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  Currency,
  SerializableCurrency,
  currencyInputSchema,
} from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

function createFormSchema(capitalizedCosts: Currency | null) {
  // An asset without capitalized costs has nothing left to impair.
  const impairmentCap =
    capitalizedCosts === null ? new Decimal(0) : capitalizedCosts.amount;

  return z
    .object({
      adjustmentType: costAdjustmentTypeSchema,
      effectiveDate: dateSchema
        .nullable()
        .refine((date): date is DateTime => date !== null, {
          message: 'Effective date is required',
        })
        .refine((date) => date >= MINIMUM_DATE, {
          message: 'Effective date cannot be before January 1, 1970',
        })
        .refine((date) => date <= DateTime.now().endOf('day'), {
          message: 'Effective date cannot be in the future',
        }),
      amount: currencyInputSchema,
      reason: z.string().nullable(),
      notes: z.string().nullable(),
    })
    .superRefine((value, ctx) => {
      const validReasons = getCostAdjustmentReasonOptions(
        value.adjustmentType
      ).map((option) => option.value as string);

      if (value.reason === null || value.reason === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Reason is required',
          path: ['reason'],
        });
      } else if (!validReasons.includes(value.reason)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Select a valid reason for this adjustment type',
          path: ['reason'],
        });
      }

      // The amount is a currency object; a failed parse leaves a non-Decimal
      // sentinel carrying its own issue, so guard before the cross-field checks.
      const amount = value.amount.amount;
      const amountIsDecimal = Decimal.isDecimal(amount) && amount.isFinite();

      if (amountIsDecimal && !amount.greaterThan(0)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Amount must be greater than zero',
          path: ['amount', 'amount'],
        });
      }

      if (
        amountIsDecimal &&
        value.adjustmentType === 'IMPAIRMENT' &&
        amount.greaterThan(impairmentCap)
      ) {
        ctx.addIssue({
          code: 'custom',
          message:
            "Impairment amount cannot exceed the asset's capitalized cost basis",
          path: ['amount', 'amount'],
        });
      }

      if (
        value.reason === 'OTHER' &&
        (!value.notes || value.notes.trim() === '')
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Notes are required when reason is Other',
          path: ['notes'],
        });
      }
    });
}

// The form holds the amount as its input shape ({ amount, isoCode }); FormOutput
// is the post-resolver shape where the amount is a parsed Currency.
interface FormData {
  adjustmentType: CostAdjustmentType;
  effectiveDate: DateTime | null;
  amount: SerializableCurrency;
  reason: string | null;
  notes: string | null;
}

type FormOutput = z.output<ReturnType<typeof createFormSchema>>;

interface Props {
  amountError: string | null;
  capitalizedCosts: Currency | null;
  costCurrencyCode: IsoCurrencyCode;
  isOpen: boolean;
  isPending: boolean;
  onClearAmountError: () => void;
  onClose: () => void;
  onSubmit: (parameters: CreateCostAdjustmentParameters) => void;
}

export function CostAdjustmentDialog(props: Props) {
  const formSchema = useMemo(
    () => createFormSchema(props.capitalizedCosts),
    [props.capitalizedCosts]
  );
  const form = useForm<FormData, unknown, FormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      adjustmentType: 'CAPITALIZATION',
      effectiveDate: DateTime.now().startOf('day'),
      amount: { amount: '', isoCode: props.costCurrencyCode },
      reason: null,
      notes: '',
    },
  });

  const adjustmentType = form.watch('adjustmentType');
  const reason = form.watch('reason');
  const reasonOptions = useMemo(
    () => getCostAdjustmentReasonOptions(adjustmentType),
    [adjustmentType]
  );
  const notesLabel = reason === 'OTHER' ? 'Notes' : 'Notes (optional)';

  useEffect(() => {
    const currentReason = form.getValues('reason');
    const isValidReason = reasonOptions.some(
      (option) => option.value === currentReason
    );

    if (currentReason !== null && !isValidReason) {
      form.setValue('reason', null);
    }
  }, [form, reasonOptions]);

  useEffect(() => {
    if (props.amountError !== null) {
      form.setError('amount.amount', {
        type: 'server',
        message: props.amountError,
      });
    }
  }, [form, props.amountError]);

  useEffect(() => {
    if (!props.isOpen) {
      form.reset({
        adjustmentType: 'CAPITALIZATION',
        effectiveDate: DateTime.now().startOf('day'),
        amount: { amount: '', isoCode: props.costCurrencyCode },
        reason: null,
        notes: '',
      });
    }
  }, [form, props.costCurrencyCode, props.isOpen]);

  const clearAmountError = () => {
    if (props.amountError !== null) {
      props.onClearAmountError();
      form.clearErrors('amount.amount');
    }
  };

  // FormCurrencyTextField has no onChange hook, so clear a stale server error
  // when the amount is edited (not on mount) via a field-change subscription.
  const amountError = props.amountError;
  const onClearAmountError = props.onClearAmountError;
  useEffect(() => {
    const subscription = form.watch((_, info) => {
      if (info.name === 'amount.amount' && amountError !== null) {
        onClearAmountError();
        form.clearErrors('amount.amount');
      }
    });
    return () => subscription.unsubscribe();
  }, [form, amountError, onClearAmountError]);

  const handleAdjustmentTypeChange = (_value: CostAdjustmentType) => {
    clearAmountError();
  };

  const handleEffectiveDateChange = (_date: DateTime | null) => {
    clearAmountError();
  };

  const handleSubmit: SubmitHandler<FormOutput> = (data) => {
    clearAmountError();
    props.onSubmit({
      adjustmentType: data.adjustmentType,
      effectiveDate: data.effectiveDate,
      amount: data.amount,
      reason: data.reason as NonNullable<
        CreateCostAdjustmentParameters['reason']
      >,
      notes: data.notes?.trim() || null,
    });
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={props.onClose}>
      <DialogTitle>Record cost adjustment</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info">
          <AlertTitle variant="subtitle2">
            Record an adjustment to the asset's cost basis
          </AlertTitle>
          <Typography variant="body2">
            A <b>capitalization</b> adjustment will <b>increase</b> the asset
            cost basis. An <b>impairment</b> adjustment will <b>decrease</b> the
            asset cost basis.
          </Typography>
        </Alert>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <Typography variant="subtitle2">Adjustment type</Typography>
          <Box
            sx={{
              '& .MuiToggleButtonGroup-root': {
                width: '100%',
              },
              '& .MuiToggleButton-root': {
                flex: 1,
              },
            }}
          >
            <Controller
              control={form.control}
              name="adjustmentType"
              render={({ field }) => (
                <FilterToggleGroup
                  ariaLabel="Cost adjustment type"
                  onChange={(value) => {
                    field.onChange(value);
                    handleAdjustmentTypeChange(value);
                  }}
                  options={
                    COST_ADJUSTMENT_TYPE_OPTIONS as FilterToggleGroupProps<CostAdjustmentType>['options']
                  }
                  value={field.value}
                />
              )}
            />
          </Box>

          <Typography variant="subtitle2">Adjustment details</Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'minmax(0, 1fr) minmax(0, 1fr)',
              },
            }}
          >
            <FormDatePicker
              control={form.control}
              disableFuture
              label="Effective date"
              name="effectiveDate"
              onChangeComplete={handleEffectiveDateChange}
            />
            <FormCurrencyTextField
              compact
              control={form.control}
              disabledTooltip="Adjustments are recorded in the asset's cost currency. Change it from the accounting details."
              fullWidth
              label="Amount"
              lockTo={props.costCurrencyCode}
              name="amount"
            />
          </Box>
          <Controller
            control={form.control}
            name="reason"
            render={({ field }) => (
              <Autocomplete
                disableClearable={false}
                freeSolo={false}
                fullWidth
                options={reasonOptions}
                value={
                  reasonOptions.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                onChange={(_, value) => field.onChange(value?.value ?? null)}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(form.formState.errors.reason)}
                    helperText={form.formState.errors.reason?.message}
                    label="Reason"
                    size="small"
                  />
                )}
              />
            )}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            error={Boolean(form.formState.errors.notes)}
            helperText={form.formState.errors.notes?.message}
            label={notesLabel}
            {...form.register('notes')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={props.isPending}
          onClick={form.handleSubmit(handleSubmit)}
        >
          Record adjustment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
